import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useSelectPatientsListColumns, columnLabelKey } from "@/hooks/useSelectPatientsListColumns.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import type { Profession } from "@/models/ProfessionModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { TooltipMenuAction } from "@/uiKit/atoms/TooltipMenu.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";
import type { PatientListMenuItems } from "@/uiKit/organisms/PatientsList/PatientListMenu.tsx";

export type PatientListColumnVisibilityMenuProps = {
  id?: string;
  qualification: Profession;
  hasMultipleDoctors?: boolean;
  tab: PatientListMenuItems;
  className?: string;
};

export const PatientListColumnVisibilityMenu: React.FC<PatientListColumnVisibilityMenuProps> = ({
  id,
  qualification,
  hasMultipleDoctors = false,
  tab,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const me = Queries.user.usePractitionerMe({});
  const updateVisibility = Queries.user.useUpdatePatientListColumnVisibility();
  const resetTab = Queries.user.useResetTabToDefaults();
  const columns = useSelectPatientsListColumns({ qualification, hasMultipleDoctors, tab });

  const togglableColumns = columns.availableColumns.filter(col => !columns.mandatoryColumns.includes(col));

  return (
    <TooltipMenuAction
      popupId={suffixId(id, "menu")}
      placement="bottom-end"
      className={cx(styles.wrapper, className)}
      actions={
        <>
          <Typography variant="body" className={styles.title}>
            {t("pages.patients.columnVisibility.title")}
          </Typography>
          {togglableColumns.map(col => {
            const visible = columns.displayedColumns.includes(col);
            return (
              <div
                key={col}
                id={suffixId(id, `toggle-${col}`)}
                onClick={() => updateVisibility.mutate({ userId: me.id, tab, key: col, state: !visible })}
                className={styles.action}
              >
                <Typography variant="body">{t(columnLabelKey[col])}</Typography>
                {visible ? (
                  <Icons.eye className={styles.icon} />
                ) : (
                  <Icons.eyeCrossed className={cx(styles.icon, styles.iconSecondary)} />
                )}
              </div>
            );
          })}
          <div
            id={suffixId(id, "reset")}
            onClick={() => resetTab.mutate({ userId: me.id, tab })}
            className={styles.reset}
          >
            <Icons.clock className={styles.icon} />
            <Typography variant="body">{t("pages.patients.columnVisibility.restoreDefaults")}</Typography>
          </div>
        </>
      }
    >
      <Button
        id={id}
        variant="text"
        className={styles.toggleButton}
        data-action="patients.columns.toggle"
        startIcon={<Icons.cog className={styles.icon} />}
        aria-label={t("pages.patients.columnVisibility.toggle")}
      />
    </TooltipMenuAction>
  );
};

const makeStyles = (theme: Theme) => ({
  wrapper: css({
    alignSelf: "center",
  }),
  action: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: theme.spacing(2),
  }),
  title: css({
    fontWeight: "bold",
  }),
  reset: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    color: theme.palette.text.secondary,
    gap: theme.spacing(4),
  }),
  icon: css({
    width: FontSizes.bodyLarge,
    height: FontSizes.bodyLarge,
  }),
  iconSecondary: css({
    color: theme.palette.text.disabled,
  }),
  toggleButton: css({
    padding: theme.spacing(6),
    height: FontSizes.bodyLarge,
    width: FontSizes.bodyLarge,
  }),
});
